import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Language } from '../i18n/translations';
import { MenuIcon, XIcon, ChevronDownIcon, GlobeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
export function Navbar() {
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close lang menu on click outside
  useEffect(() => {
    const handleClick = () => setIsLangMenuOpen(false);
    if (isLangMenuOpen) {
      setTimeout(() => document.addEventListener('click', handleClick), 0);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [isLangMenuOpen]);
  const isHome = location.pathname === '/';
  const showSolid = isScrolled || !isHome;
  const navLinks = [{
    name: t.nav.home,
    href: '/'
  }, {
    name: t.nav.about,
    href: '/about'
  }, {
    name: t.nav.menu,
    href: '/menu'
  }, {
    name: t.nav.pool,
    href: '/pool'
  }, {
    name: t.nav.reservations,
    href: '/reservations'
  }, {
    name: t.nav.gallery,
    href: '/gallery'
  }, {
    name: t.nav.contact,
    href: '/contact'
  }];
  const languages: {
    code: Language;
    label: string;
  }[] = [{
    code: 'es',
    label: 'ES'
  }, {
    code: 'ca',
    label: 'CA'
  }, {
    code: 'en',
    label: 'EN'
  }];
  return <nav className={`fixed w-full z-50 transition-all duration-500 ease-elegant ${showSolid ? 'bg-navy/95 backdrop-blur-md py-3 shadow-elegant' : 'bg-gradient-to-b from-navy/40 to-transparent py-6'}`} data-id="element-199">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-200">
        <div className="flex justify-between items-center" data-id="element-201">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0 flex items-center gap-3 group" data-id="element-202">
            <img src="https://cdn.magicpatterns.com/uploads/rvUr1hx9uLnf4XdJqeJvHW/278246131_375859524423516_8914442802232856168_n.jpg" alt="El Astillero Logo" className="h-9 w-9 rounded-full border border-white/20 transition-transform duration-300 group-hover:scale-105" data-id="element-203" />
            <span className="font-serif text-lg font-medium tracking-wide text-white" data-id="element-204">
              El Astillero
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center space-x-8" data-id="element-205">
            {navLinks.map(link => {
            const isActive = location.pathname === link.href;
            return <Link key={link.href} to={link.href} className={`nav-link-underline text-[13px] font-medium tracking-[0.08em] uppercase transition-colors duration-500 ${isActive ? 'text-sand active' : 'text-white/80 hover:text-sand'}`} data-id="element-206">
                  {link.name}
                </Link>;
          })}
          </div>

          {/* Right side */}
          <div className="hidden lg:flex items-center space-x-6" data-id="element-207">
            {/* Language Selector */}
            <div className="relative" data-id="element-208">
              <button onClick={e => {
              e.stopPropagation();
              setIsLangMenuOpen(!isLangMenuOpen);
            }} className="flex items-center space-x-1.5 text-white/70 hover:text-sand transition-colors duration-500" data-id="element-209">
                <GlobeIcon className="w-3.5 h-3.5" data-id="element-210" />
                <span className="text-xs font-medium tracking-wider uppercase" data-id="element-211">
                  {language}
                </span>
                <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-500 ${isLangMenuOpen ? 'rotate-180' : ''}`} data-id="element-212" />
              </button>

              <AnimatePresence data-id="element-213">
                {isLangMenuOpen && <motion.div initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: 8
              }} transition={{
                duration: 0.2
              }} className="absolute right-0 mt-3 w-20 bg-white rounded shadow-elegant py-1 border border-gray-100/50" data-id="element-214">
                    {languages.map(lang => <button key={lang.code} onClick={() => {
                  setLanguage(lang.code);
                  setIsLangMenuOpen(false);
                }} className={`block w-full text-center px-3 py-2 text-xs tracking-wider ${language === lang.code ? 'text-sand font-semibold bg-sand/5' : 'text-dark/70 hover:bg-gray-50 hover:text-navy'}`} data-id="element-215">
                        {lang.label}
                      </button>)}
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* CTA Button */}
            <Link to="/reservations" className="bg-sand hover:bg-sand-dark text-navy font-medium px-7 py-2.5 text-[11px] tracking-[0.15em] uppercase transition-all duration-700 ease-elegant hover:shadow-warm hover:scale-[1.02]" data-id="element-216">
              {t.nav.bookNow}
            </Link>
          </div>

          {/* Mobile menu button */}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden text-white hover:text-sand focus:outline-none transition-colors duration-500" data-id="element-217">
            {isMobileMenuOpen ? <XIcon className="h-6 w-6" data-id="element-218" /> : <MenuIcon className="h-6 w-6" data-id="element-219" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence data-id="element-220">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} transition={{
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }} className="lg:hidden bg-navy/98 backdrop-blur-lg border-t border-white/5" data-id="element-221">
            <div className="px-6 pt-4 pb-8 space-y-1" data-id="element-222">
              {navLinks.map(link => <Link key={link.href} to={link.href} onClick={() => setIsMobileMenuOpen(false)} className={`block px-4 py-3.5 text-sm font-medium tracking-wide rounded transition-colors ${location.pathname === link.href ? 'text-sand bg-white/5' : 'text-white/80 hover:text-sand hover:bg-white/5'}`} data-id="element-223">
                  {link.name}
                </Link>)}

              <div className="px-4 py-4 flex space-x-5 border-t border-white/10 mt-3" data-id="element-224">
                {languages.map(lang => <button key={lang.code} onClick={() => {
              setLanguage(lang.code);
              setIsMobileMenuOpen(false);
            }} className={`text-xs font-medium tracking-wider uppercase ${language === lang.code ? 'text-sand' : 'text-white/50 hover:text-white'}`} data-id="element-225">
                    {lang.label}
                  </button>)}
              </div>

              <Link to="/reservations" onClick={() => setIsMobileMenuOpen(false)} className="block w-full text-center mt-4 bg-sand text-navy font-medium px-6 py-3.5 text-sm tracking-wider uppercase" data-id="element-226">
                {t.nav.bookNow}
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}